/**
 * Sidebar Component Registry
 *
 * Allows packages to register Vue components that can be rendered
 * dynamically in the Portal sidebar.
 */

import type { Component } from "vue";

const registry = new Map<string, Component>();

/**
 * Register a sidebar component.
 *
 * @param name Component name (must match component name from PortalLayoutRegistry)
 * @param component Vue component
 *
 * @example
 * ```ts
 * import { registerSidebarComponent } from '@ja/portal/lib/sidebarComponents';
 * import SidebarRecentItems from './components/SidebarRecentItems.vue';
 *
 * registerSidebarComponent('SidebarRecentItems', SidebarRecentItems);
 * ```
 */
export function registerSidebarComponent(
    name: string,
    component: Component
): void {
    registry.set(name, component);
}

/**
 * Get a registered sidebar component by name.
 */
export function getSidebarComponent(name: string): Component | undefined {
    return registry.get(name);
}

/**
 * Check if a component is registered.
 */
export function hasSidebarComponent(name: string): boolean {
    return registry.has(name);
}

/**
 * Get all registered component names.
 */
export function getRegisteredComponents(): string[] {
    return Array.from(registry.keys());
}
